/**
 * Who is signed in, and the way in to your own account.
 *
 * It replaces what used to be a plain label in the top bar, because the label
 * was already the one thing on every screen that says "this is you", and a
 * second control beside it saying the same thing twice is how a top bar gets
 * crowded. Pressing it opens Your account.
 *
 * Everybody gets it, every role: the dialog behind it changes nothing but your
 * own username and password.
 *
 * The identity line itself is still composed in `lib\session.ts` (R8: the role
 * reads in this instance's vocabulary), so this file decides where the words go
 * and never what they say. Below `sm` the words give way to an icon and the
 * accessible name is restored in text, which is the rule the sign-out and
 * feedback buttons already follow: `title` is not a name a screen reader can be
 * relied on to read, and it is unreachable by keyboard or touch anyway.
 */

import { UserCog } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui"
import { sessionLabel, type Me } from "@/lib/session"

import { AccountSettingsDialog } from "./account-settings-dialog"

export function AccountButton({ me }: { me: Me }) {
  const [open, setOpen] = React.useState(false)
  const [announcement, setAnnouncement] = React.useState("")

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          // Cleared on the way in: a live region announces what changed, so a
          // sentence left over from last time would silence the next one.
          setAnnouncement("")
          setOpen(true)
        }}
        aria-haspopup="dialog"
      >
        <UserCog className="h-4 w-4" aria-hidden />
        <span className="hidden max-w-[22ch] truncate sm:inline">
          {sessionLabel(me)}
        </span>
        <span className="sr-only sm:hidden">Your account</span>
      </Button>

      {open && (
        <AccountSettingsDialog
          me={me}
          onClose={() => setOpen(false)}
          onDone={setAnnouncement}
        />
      )}

      {/* Outside the dialog on purpose: a live region that unmounts with the
          dialog it sits in can be cut off before it has spoken. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </>
  )
}
